import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  Banknote,
  Bike,
  Inbox,
  Scale,
  Wallet,
  Store,
  Users as UsersIcon,
  Moon,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiGet, fetchLeads, ADMIN_API_URL } from "@/admin/adminApi";
import { MOCK } from "@/admin/mock";
import { useAdmin } from "@/admin/AdminLayout";
import {
  PageHeader,
  StatCard,
  StatusBadge,
  SimpleTable,
  Loading,
  money,
  fmtDate,
} from "@/admin/adminComponents";

const RAMADAN_LS = "halalgo_ramadan_settings";

const readRamadan = () => {
  const local = localStorage.getItem(RAMADAN_LS);
  return local ? { ...MOCK.ramadan, ...JSON.parse(local) } : MOCK.ramadan;
};

const recentOrderColumns = [
  { key: "id", label: "Order", render: (r) => <span className="font-mono text-xs">{r.id}</span> },
  { key: "customer_name", label: "Customer" },
  { key: "restaurant_name", label: "Restaurant" },
  { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
  { key: "total", label: "Total", render: (r) => <span className="font-semibold">{money(r.total, r.currency)}</span> },
];

export default function Overview() {
  const { token } = useAdmin();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      const [o, u, r, rd, dp, s] = await Promise.all([
        apiGet("/orders", MOCK.orders),
        apiGet("/users", MOCK.users),
        apiGet("/restaurants", MOCK.restaurants),
        apiGet("/riders", MOCK.riders),
        apiGet("/disputes", MOCK.disputes),
        apiGet("/settlements", MOCK.settlements),
      ]);
      let leads = null;
      try {
        leads = await fetchLeads(token);
      } catch (e) {
        leads = null;
      }
      if (!mountedRef.current) return;
      setD({
        orders: o.data || [],
        users: u.data || [],
        restaurants: r.data || [],
        riders: rd.data || [],
        disputes: dp.data || [],
        settlements: s.data || [],
        mocked: o.mocked,
        leads,
      });
      if (!silent) setLoading(false);
    },
    [token]
  );

  const reload = useCallback(async () => {
    setRefreshing(true);
    await load(true);
    if (mountedRef.current) setRefreshing(false);
  }, [load]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Live Data Cutover: silently re-check for the live API while on mock data.
  useEffect(() => {
    if (!d?.mocked || !ADMIN_API_URL) return undefined;
    const id = setInterval(() => load(true), 20000);
    return () => clearInterval(id);
  }, [d?.mocked, load]);

  if (loading || !d) {
    return (
      <div data-testid="admin-overview-page">
        <PageHeader title="Overview" subtitle="Business snapshot across the Halal GO network." />
        <Loading label="Loading dashboard..." />
      </div>
    );
  }

  const grossSales = d.orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
  const activeRiders = d.riders.filter((r) => r.status === "active").length;
  const pendingDisputes = d.disputes.filter((x) => x.status === "pending_review").length;
  const unpaidTotal = d.settlements.filter((x) => x.status === "unpaid").reduce((sum, x) => sum + Number(x.amount_due || 0), 0);
  const leadCounts = d.leads?.counts || {};
  const leadsTotal = Object.values(leadCounts).reduce((a, b) => a + Number(b || 0), 0);
  const ramadan = readRamadan();

  const leadBreakdown = [
    { label: "Waitlist", value: leadCounts.waitlist ?? 0, icon: Inbox },
    { label: "Rider Applications", value: leadCounts.riders ?? 0, icon: Bike },
    { label: "Restaurant Partners", value: leadCounts.merchants ?? 0, icon: Store },
    { label: "Contact Messages", value: leadCounts.contacts ?? 0, icon: UsersIcon },
  ];

  return (
    <div data-testid="admin-overview-page">
      <PageHeader
        title="Overview"
        subtitle="Business snapshot across the Halal GO network."
        mocked={d.mocked}
        actions={
          <Button variant="outline" size="sm" onClick={reload} disabled={refreshing} data-testid="admin-refresh-button" className="border-halalgo-kraft-200">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {ramadan.ramadan_mode_active ? (
        <Link to="/admin/settings" className="mb-6 flex items-center gap-3 rounded-xl border border-halalgo-green-200 bg-halalgo-green-50 px-4 py-3 text-sm text-halalgo-green-800 transition-colors hover:bg-halalgo-green-100" data-testid="overview-ramadan-banner">
          <Moon className="h-5 w-5" />
          <span className="font-semibold">Ramadan Mode is active.</span>
          <span className="hidden text-halalgo-green-800/70 sm:inline">{ramadan.announcement_banner}</span>
          <ArrowRight className="ml-auto h-4 w-4" />
        </Link>
      ) : null}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ShoppingBag} label="Orders" value={d.orders.length} hint="Recent window" />
        <StatCard icon={Banknote} label="Gross Sales" value={money(grossSales)} hint="Across shown orders" />
        <StatCard icon={Bike} label="Active Riders" value={activeRiders} hint={`${d.riders.length} total in fleet`} />
        <StatCard icon={Inbox} label="Website Leads" value={d.leads ? leadsTotal : "\u2014"} hint={d.leads ? "From marketing site" : "Unavailable"} accent="kraft" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Scale} label="Pending Disputes" value={pendingDisputes} accent={pendingDisputes ? "amber" : "green"} />
        <StatCard icon={Wallet} label="Unpaid Settlements" value={money(unpaidTotal)} accent={unpaidTotal ? "amber" : "green"} />
        <StatCard icon={Store} label="Restaurants" value={d.restaurants.length} />
        <StatCard icon={UsersIcon} label="Users" value={d.users.length} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold text-halalgo-ink">Recent Orders</h2>
            <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm font-medium text-halalgo-green-700 hover:text-halalgo-green-800" data-testid="overview-view-orders">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <SimpleTable columns={recentOrderColumns} rows={d.orders.slice(0, 5)} testId="overview-recent-orders" />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold text-halalgo-ink">Website Leads</h2>
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm font-medium text-halalgo-green-700 hover:text-halalgo-green-800" data-testid="overview-view-leads">
              Manage <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-2 rounded-xl border border-halalgo-kraft-100 bg-white p-3 shadow-lift-sm">
            {leadBreakdown.map((l) => (
              <div key={l.label} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-halalgo-green-50/40">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-halalgo-kraft-50 text-halalgo-kraft-700">
                  <l.icon className="h-4 w-4" />
                </span>
                <span className="text-sm text-halalgo-ink/80">{l.label}</span>
                <span className="ml-auto text-base font-bold text-halalgo-ink">{d.leads ? l.value : "\u2014"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
